import { useState, useEffect, useMemo } from 'react';
import type {
  AgentApiCandidateFormItemsShow,
  AgentApiCandidateFormsShow,
} from '@/types/serializers';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { Tag } from '@/components/Tag';
import { Button } from '@/components/Button';
import { LuX } from 'react-icons/lu';
import { MdDelete } from 'react-icons/md';
import useFetchCandidateProfilesCustomFields from '@/features/agent/candidates/api/useFetchCandidateProfilesCustomFields';
import {
  candidateFormItemFormatTypeOptions,
  getCandidateFormItemFormTypeLabel,
} from '@/features/agent/form/candidateFormItemOptions';
import useUpdateCandidateFormItem from '@/features/agent/form/api/useUpdateCandidateFormItem';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';

import ChangeInfoTypeDialog from './modals/ChangeInfoTypeDialog';
import DeleteItemDialog from './modals/DeleteItemDialog';

interface Props {
  formItem?: AgentApiCandidateFormItemsShow;
  candidateFormId: number;
  candidateForm?: AgentApiCandidateFormsShow;
  onItemDeleted?: () => void;
  onFormItemChange?: (updatedFormItem: Partial<AgentApiCandidateFormItemsShow>) => void;
  onItemSaved?: () => void;
}

// シンプルなToggleコンポーネント
function Toggle({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative h-5 w-10 rounded-full transition-colors duration-200 focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${
        checked ? 'bg-primary' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 size-4 transform rounded-full bg-white transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export default function EditCard({
  formItem,
  candidateFormId,
  candidateForm,
  onItemDeleted,
  onFormItemChange,
  onItemSaved,
}: Props) {
  const [title, setTitle] = useState(formItem?.title || '');
  const [required, setRequired] = useState(formItem?.required || false);
  const [placeholder, setPlaceholder] = useState(formItem?.placeholder || '');
  const [description, setDescription] = useState(formItem?.description || '');
  const [suffix, setSuffix] = useState(formItem?.suffix || '');
  const [formatType, setFormatType] = useState(formItem?.format_type || 'nothing');
  const [minLength, setMinLength] = useState<string>(formItem?.min_length?.toString() || '');
  const [maxLength, setMaxLength] = useState<string>(formItem?.max_length?.toString() || '');
  const [customFieldId, setCustomFieldId] = useState<string>('');
  const [options, setOptions] = useState<string[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isChangeInfoTypeDialogOpen, setIsChangeInfoTypeDialogOpen] = useState(false);

  const { mutateAsync: updateItem, isPending: isUpdating } = useUpdateCandidateFormItem();

  // カスタムフィールドのリストを取得
  const { data: customFieldsData } = useFetchCandidateProfilesCustomFields();
  const customFields = customFieldsData?.records || [];

  // 既に使用されているcustom_field_idを取得（現在のformItem以外）
  const usedCustomFieldIds = useMemo(() => {
    if (!candidateForm?.form_items) return new Set<number>();
    return new Set(
      candidateForm.form_items
        .filter((item) => item.id !== formItem?.id && item.custom_field_id !== null)
        .map((item) => item.custom_field_id as number)
    );
  }, [candidateForm?.form_items, formItem?.id]);

  // カスタムフィールドの選択肢を生成（既に使用されているものは無効化）
  const customFieldOptions = useMemo(() => {
    return customFields.map((field) => {
      const isUsed = usedCustomFieldIds.has(field.id);
      return {
        value: field.id.toString(),
        label: field.name,
        disabled: isUsed,
      };
    });
  }, [customFields, usedCustomFieldIds]);

  // 現在選択されているカスタムフィールドを検索（custom_field_nameから）
  const currentCustomField = useMemo(() => {
    if (!formItem?.custom_field_name) return null;
    return customFields.find((field) => field.name === formItem.custom_field_name) || null;
  }, [customFields, formItem?.custom_field_name]);

  // formItemが変更されたときに各フィールドを更新
  useEffect(() => {
    if (formItem) {
      setTitle(formItem.title || '');
      // 氏名の場合は常に必須（required = true）に固定
      setRequired(formItem.item_type === 'candidate_name' ? true : formItem.required || false);
      setPlaceholder(formItem.placeholder || '');
      setDescription(formItem.description || '');
      setSuffix(formItem.suffix || '');
      setFormatType(formItem.format_type || 'nothing');
      setMinLength(formItem.min_length?.toString() || '');
      setMaxLength(formItem.max_length?.toString() || '');
      setOptions(
        Array.isArray(formItem.select_options) ? (formItem.select_options as string[]) : []
      );
      // custom_field_idが直接指定されている場合はそれを使う、なければcustom_field_nameから検索
      if (formItem.custom_field_id) {
        setCustomFieldId(formItem.custom_field_id.toString());
      } else if (currentCustomField) {
        setCustomFieldId(currentCustomField.id.toString());
      } else {
        setCustomFieldId('');
      }
    }
  }, [formItem, currentCustomField]);

  // 氏名の場合はrequiredをtrueに固定
  useEffect(() => {
    if (formItem?.item_type === 'candidate_name' && !required) {
      setRequired(true);
    }
  }, [formItem?.item_type, required]);

  // 編集内容が変更されたらプレビューに反映
  useEffect(() => {
    if (!formItem || !onFormItemChange) return;

    const updatedItem: Partial<AgentApiCandidateFormItemsShow> = {
      ...formItem,
      title,
      required,
      placeholder: placeholder || undefined,
      description: description || undefined,
      suffix: suffix || undefined,
      format_type: formatType,
      min_length: minLength ? parseInt(minLength, 10) : undefined,
      max_length: maxLength ? parseInt(maxLength, 10) : undefined,
      custom_field_id: customFieldId ? parseInt(customFieldId, 10) : undefined,
    };

    // manualの選択系項目のみ、プレビューの選択肢を上書き
    if (
      formItem.item_type === 'manual' &&
      (formItem.form_type === 'selectbox' ||
        formItem.form_type === 'radio' ||
        formItem.form_type === 'checkbox')
    ) {
      updatedItem.select_options = options;
    }

    onFormItemChange(updatedItem);
    // formItemの変更時は呼び出さない（初期化時のみ）
  }, [
    title,
    required,
    placeholder,
    description,
    suffix,
    formatType,
    minLength,
    maxLength,
    customFieldId,
    options,
  ]); // onFormItemChangeは依存配列から除外

  if (!formItem) {
    return (
      <div className="mt-6 w-full">
        <label className="mb-4 block text-heading-xxs text-main">編集</label>
        <div className="w-full rounded-sm bg-white p-lg shadow-layer1">
          <p className="text-body-sm text-sub">項目を選択してください</p>
        </div>
      </div>
    );
  }

  const isText = formItem.form_type === 'text';
  const isTextOrTextarea = isText || formItem.form_type === 'textarea';
  const isSelectable =
    formItem.form_type === 'selectbox' ||
    formItem.form_type === 'radio' ||
    formItem.form_type === 'checkbox';
  const isShowOptionsEditor = formItem.item_type === 'manual' && isSelectable;
  const isShowPlaceholder = isTextOrTextarea || formItem.form_type === 'selectbox';
  const maxLengthLimit = formItem.form_type === 'textarea' ? '2000' : '150';
  const maxLengthPlaceholder = formItem.form_type === 'textarea' ? '2000' : '150';

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  return (
    <div className="mt-md w-full">
      <div className="w-full rounded-sm bg-white p-lg shadow-layer1">
        <label className="mb-4 block text-heading-xxs text-main">質問文</label>
        <Input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="質問文を入力してください"
          className="w-full"
        />

        <div className="mt-6 flex gap-6">
          {/* 左側カラム */}
          <div className="flex w-[236px] flex-col gap-5">
            {/* 必須項目 */}
            <div className="flex items-center gap-4">
              <label className="w-[70px] text-heading-xxs text-main">必須項目</label>
              <Toggle
                checked={required}
                onChange={setRequired}
                disabled={formItem.item_type === 'candidate_name'}
              />
            </div>
            {/* 情報タイプ */}
            <div className="flex items-center gap-4">
              <label className="w-[70px] text-heading-xxs text-main">情報タイプ</label>
              <Tag color="gray" size="sm">
                {getCandidateFormItemFormTypeLabel(formItem.form_type)}
              </Tag>
            </div>
            {/* 最小文字数・最大文字数（textとtextareaのみ表示） */}
            {isTextOrTextarea && (
              <>
                <div className="flex items-center gap-4">
                  <label className="w-[70px] text-heading-xxs text-main">最小文字数</label>
                  <Input
                    type="number"
                    value={minLength}
                    onChange={(e) => {
                      const newMinLength = e.target.value;
                      setMinLength(newMinLength);
                      // 最小文字数が最大文字数を超える場合は、最大文字数を最小文字数に合わせる
                      if (
                        newMinLength &&
                        maxLength &&
                        parseInt(newMinLength, 10) > parseInt(maxLength, 10)
                      ) {
                        setMaxLength(newMinLength);
                      }
                    }}
                    placeholder="0"
                    className="w-[140px]"
                    min="0"
                    max={maxLengthLimit}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="w-[70px] text-heading-xxs text-main">最大文字数</label>
                  <Input
                    type="number"
                    value={maxLength}
                    onChange={(e) => {
                      const newMaxLength = e.target.value;
                      setMaxLength(newMaxLength);
                      // 最大文字数が最小文字数を下回る場合は、最小文字数を最大文字数に合わせる
                      if (
                        newMaxLength &&
                        minLength &&
                        parseInt(newMaxLength, 10) < parseInt(minLength, 10)
                      ) {
                        setMinLength(newMaxLength);
                      }
                    }}
                    placeholder={maxLengthPlaceholder}
                    className="w-[140px]"
                    min="0"
                    max={maxLengthLimit}
                  />
                </div>
              </>
            )}
          </div>

          {/* 右側カラム */}
          <div className="flex flex-1 flex-col gap-md border-l border-solid border-border pl-lg">
            {isShowOptionsEditor && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-heading-xxs text-main">選択肢</label>
                  <button
                    type="button"
                    onClick={addOption}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + 選択肢を追加
                  </button>
                </div>
                <div className="space-y-2">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`選択肢${index + 1}`}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="rounded p-1 hover:bg-gray-100"
                      >
                        <LuX size={16} className="text-gray-600" />
                      </button>
                    </div>
                  ))}
                  {options.length === 0 && (
                    <p className="text-body-sm text-sub">選択肢を追加してください</p>
                  )}
                </div>
              </div>
            )}
            {/* 注釈 */}
            <div className="flex flex-col gap-2">
              <label className="text-heading-xxs text-main">注釈</label>
              <Input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="質問文の下に表示する補足説明を入力してください"
                maxLength={50}
                className="w-full"
              />
              <p className="text-body-sm text-sub">50文字以内</p>
            </div>
            {/* プレースホルダー */}
            {isShowPlaceholder && (
              <div className="flex flex-col gap-2">
                <label className="text-heading-xxs text-main">プレースホルダー</label>
                <Input
                  type="text"
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
            {isText && (
              <>
                {/* フォーマット制限 */}
                <div className="flex w-[236px] flex-col gap-2">
                  <label className="text-heading-xxs text-main">フォーマット制限</label>
                  <Select
                    items={candidateFormItemFormatTypeOptions}
                    value={formatType}
                    onValueChange={(value) => setFormatType(value as typeof formatType)}
                    defaultLabel="指定なし"
                    className="w-full"
                    disabled={formItem.item_type === 'candidate_name'}
                  />
                </div>

                {/* フォーム末尾（textのみ） */}
                {formItem.form_type === 'text' && (
                  <div className="flex flex-col gap-2">
                    <label className="text-heading-xxs text-main">フォーム末尾</label>
                    <Input
                      type="text"
                      value={suffix}
                      onChange={(e) => setSuffix(e.target.value)}
                      placeholder="フォームの末尾に文字を入れる場合は入力してください（万円など）"
                      className="w-full"
                    />
                  </div>
                )}
              </>
            )}

            {/* 求職者データ連携先 */}
            {formItem.item_type === 'custom_field' && (
              <div className="flex w-[236px] flex-col gap-2">
                <label className="text-heading-xxs text-main">求職者データ連携先</label>
                <Select
                  items={customFieldOptions}
                  value={customFieldId}
                  onValueChange={(value) => setCustomFieldId(value)}
                  defaultLabel="連携先を選択してください"
                  className="w-full"
                />
              </div>
            )}
          </div>
        </div>

        {/* 保存・削除・情報タイプ変更ボタン */}
        <div className="mt-6 flex items-center justify-between">
          {isSelectable && (
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={() => setIsChangeInfoTypeDialogOpen(true)}
            >
              情報タイプを変更する
            </Button>
          )}
          <div className="ml-auto flex gap-3">
            {formItem.item_type !== 'candidate_name' && (
              <>
                <Button
                  type="button"
                  color="danger-secondary"
                  size="md"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <MdDelete className="size-5" />
                  質問を削除する
                </Button>
                <DeleteItemDialog
                  open={isDeleteDialogOpen}
                  onOpenChange={setIsDeleteDialogOpen}
                  itemTitle={formItem.title}
                  candidateFormId={candidateFormId}
                  candidateFormItemId={formItem.id}
                  onDeleted={onItemDeleted}
                />
              </>
            )}
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={async () => {
                if (!formItem) return;
                try {
                  await updateItem({
                    candidateFormId,
                    candidateFormItemId: formItem.id,
                    title,
                    required,
                    placeholder,
                    description,
                    suffix,
                    min_length: minLength || '',
                    max_length: maxLength || '',
                    format_type: formatType,
                    custom_field_id: customFieldId ? parseInt(customFieldId, 10) : null,
                    ...(isShowOptionsEditor ? { options } : {}),
                  });
                  showSuccessToast('保存しました');
                  // 保存成功後、編集状態をリセット
                  onItemSaved?.();
                } catch (error) {
                  const errorMessage =
                    error instanceof Error ? error.message : '保存に失敗しました';
                  showErrorToast(errorMessage);
                }
              }}
              disabled={isUpdating}
            >
              {isUpdating ? '保存中...' : '保存する'}
            </Button>
          </div>
        </div>
        <ChangeInfoTypeDialog
          open={isChangeInfoTypeDialogOpen}
          onClose={() => setIsChangeInfoTypeDialogOpen(false)}
          currentFormType={formItem.form_type}
          candidateFormId={candidateFormId}
          candidateFormItemId={formItem.id}
          onSaved={onItemSaved}
        />
      </div>
    </div>
  );
}
